import React, {
  useCallback, useEffect, useState, useRef,
} from 'react';
import { useSelector, useDispatch } from 'react-redux';
import classNames from 'classnames';
import usePaymentMethodSelector from '../../hooks/usePaymentMethodsSelector';
import store from '../../store';
import {
  setNeedsToCheckPaypalShippingAddressDraft,
  setPaypalShippingAddressDraft,
} from '../../slices/shippingAddress/index.migrated';
import {
  setPaypalOrderId,
  clearPaymentMethodsData,
  paymentMethods,
} from '../../slices/payments';
import {
  savePaymentMethodData,
} from '../../slices/payments/index.migrated';
import {
  isShippingAddressValid,
} from '../../utils';
import {
  calculateTotalGiftCards,
  getLastURIComponent
} from '../../utils/index-migrated';
import storage from '../../utils/storage';
import sessionStorageService from '../../services/sessionStorage';
import styles from './PaypalButton.module.scss';
import { CartInStore } from '../../slices/cart/types';
import { Payer, PurchaseUnit } from './types';
import {
  CustomerAddress,
} from '../../slices/shippingAddress/types';

// eslint-disable-next-line max-len
const PAYPAL_URL = `https://www.paypal.com/sdk/js?client-id=${process.env.PUBLIC_PAYPAL_CLIENT_ID}&commit=false`;

type PaypalButtonProps = {
  buttonsToRenderIds?: string[];
  buttonsIdsThatReplacesDataWithTheInfoFromPaypal?: string[];
  id: string;
  isPartOfMultiButtonGroup?: boolean;
  isRendererButton?: boolean;
  onApprove?: (args: { address:  Record<string, string | number>;}) => Promise<void>;
  isSteppedCheckout?: boolean;
}

type Address = {
  streetName?: string;
  city?: string;
  state?: string;
  postalCode?: string;
  country: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  additionalStreetInfo?: string;
}


const paypalBuyerToAddress = ({ purchase_units: pu, payer }: {
  purchase_units: PurchaseUnit[];
  payer: Payer;
}) => {
  const address = pu[0]?.shipping?.address || {};
  return {
    streetAddress: address.address_line_1,
    city: address.admin_area_2,
    state: address.admin_area_1,
    postalCode: address.postal_code,
    country: address.country_code,
    firstName: payer.name.given_name,
    lastName: payer.name.surname,
    email: payer.email_address,
    phone: payer.phone?.phone_number?.national_number,
    additionalStreetInfo: payer?.address?.address_line_2,
  };
};

const addressToPaypalBuyer = (address: Address) => ({
  address_line_1: address.streetName,
  address_line_2: address.additionalStreetInfo,
  admin_area_2: address.city,
  admin_area_1: address.state,
  postal_code: address.postalCode,
  country_code: address.country,
});


const PaypalButton: React.FC<PaypalButtonProps> = ({
  id,
  isPartOfMultiButtonGroup,
  isRendererButton,
  buttonsToRenderIds,
  buttonsIdsThatReplacesDataWithTheInfoFromPaypal,
  onApprove,
  isSteppedCheckout,
}) => {
  const dispatch = useDispatch();

  const cartId = useSelector((state: { cart: CartInStore }) => state.cart.id);

  const onApproveRef = useRef(onApprove);

  const { changeSelectedPaymentMethod } = usePaymentMethodSelector();

  useEffect(() => {
    onApproveRef.current = onApprove;
  }, [onApprove]);

  const [isPaypalLibraryLoaded, setIsPaypalLibraryLoaded] = useState(false);
  const renderPaypalButton = useCallback(() => {
    if (!isPartOfMultiButtonGroup || isRendererButton) {
      const buttons = buttonsToRenderIds ?? [id];
      buttons.forEach((currentPaypalButton) => {
        if (window.paypal?.Buttons) {
          window.paypal
          .Buttons({
            style: {
              shape: 'sharp',
              height: 55,
              disableMaxWidth: true,
            },
            fundingSource: window.paypal.FUNDING.PAYPAL,
            createOrder: (_data, actions) => {
              const {
                giftCards,
                taxedPrice,
                totalPrice,
                shippingAddress,
              }: CartInStore = store.getState().cart;

              const giftCardsTotal = calculateTotalGiftCards({ giftCards });

              const totalItemPrice = taxedPrice?.totalGross || totalPrice;
              const { centAmount, currencyCode, fractionDigits } = totalItemPrice as {
                centAmount: number;
                currencyCode: string;
                fractionDigits: number;
              };
              const total = {
                value: `${(centAmount - giftCardsTotal) /
                  10 ** fractionDigits}`,
                currency_code: currencyCode,
              };

              const mustReplaceDataWithTheInfoFromPaypal =
                !!buttonsIdsThatReplacesDataWithTheInfoFromPaypal?.includes(
                  currentPaypalButton,
                );

              return actions.order.create({
                application_context: {
                  shipping_preference:
                    !mustReplaceDataWithTheInfoFromPaypal &&
                    shippingAddress &&
                    isShippingAddressValid(shippingAddress as Address) &&
                    !store.getState()?.accounts?.customerId
                      ? 'SET_PROVIDED_ADDRESS'
                      : 'GET_FROM_FILE',
                },
                purchase_units: [
                  {
                    amount: total,
                    ...(!mustReplaceDataWithTheInfoFromPaypal &&
                      shippingAddress &&
                      isShippingAddressValid(shippingAddress as Address) && {
                        shipping: {
                          address: addressToPaypalBuyer(shippingAddress as Address),
                        },
                      }),
                  },
                ],
              });
            },
            onApprove: async (_, { order }) => {
              const lastURIComponent: string = getLastURIComponent();
              const buyerData = await order.get();

              const mustReplaceDataWithTheInfoFromPaypal =
                !!buttonsIdsThatReplacesDataWithTheInfoFromPaypal?.includes(
                  currentPaypalButton,
                );

                const newShippingAddress = paypalBuyerToAddress(buyerData);
              if (mustReplaceDataWithTheInfoFromPaypal) {

                if (lastURIComponent.toLocaleLowerCase() === 'cart') {
                  sessionStorageService.setItem({
                    itemName: 'paypalShippingAddressDraft',
                    value: JSON.stringify(newShippingAddress),
                  });
                } else {
                  dispatch(setPaypalShippingAddressDraft(newShippingAddress as CustomerAddress));
                  dispatch(setNeedsToCheckPaypalShippingAddressDraft(true));
                }
              }

              if (onApprove && onApproveRef?.current) {
                const currentAddress = {
                  address: newShippingAddress as Address,
                };
                await onApproveRef.current(currentAddress);
              }

              dispatch(clearPaymentMethodsData());
              changeSelectedPaymentMethod(paymentMethods.PAYPAL);
              dispatch(savePaymentMethodData({}));

              if (lastURIComponent.toLocaleLowerCase() === 'cart') {
                storage.cleanAllPaymentData();
                localStorage.setItem('paypalOrderId', buyerData.id);
                window.location.href = '/checkout';
              } else {
                dispatch(setPaypalOrderId(buyerData?.id));
              }
            },
          })
          .render(`#${currentPaypalButton}`);
        }
      });
    }
  }, [
    buttonsIdsThatReplacesDataWithTheInfoFromPaypal,
    buttonsToRenderIds,
    changeSelectedPaymentMethod,
    dispatch,
    id,
    isPartOfMultiButtonGroup,
    isRendererButton,
    onApprove,
  ]);

  useEffect(() => {
    if (cartId && !isPaypalLibraryLoaded && isRendererButton) {
      const s = document.createElement('script');
      s.type = 'text/javascript';
      s.async = true;
      s.src = PAYPAL_URL;
      s.onload = renderPaypalButton;
      document.getElementsByTagName('body')[0].appendChild(s);
      setIsPaypalLibraryLoaded(true);
    }
  }, [cartId, isPaypalLibraryLoaded, isRendererButton, renderPaypalButton]);

  return <>
    <div id={id} className={classNames({
      [styles['paypal-pay-button-wrapper']]: !isSteppedCheckout,
      [styles['stepped-checkout']]: isSteppedCheckout,
    }
    )}
    />
    {isSteppedCheckout && <div className={styles['paypal-button']}>
      <div className={styles.logo}/>
    </div>
    }
    
  </>
};

export default PaypalButton;
